// Animation library shown in the Animate tab. `id` is a local slug; the provider
// params are carried per entry because the two engines identify a clip
// differently - Meshy takes a numeric library id (`actionId`, submitted as
// action_id; see https://docs.meshy.ai/en/api/animation-library) while Tripo3D
// retargets onto one of its named presets (`tripoPreset`, submitted as
// animation). Every preset the Tripo3D schema accepts has an entry here, so no
// preset is unreachable; entries without a `tripoPreset` can only run on Meshy,
// whose catalogue is far larger. Grouped by intent (idles, gestures, combat,
// locomotion, body movements, dance) rather than alphabetically.
const PREVIEW_PATH = '/assets/three_d_studio/animate_tab'

const animation = (id, name, { actionId, tripoPreset }) => ({
  id,
  name,
  actionId,
  tripoPreset,
  preview: `${PREVIEW_PATH}/${id.replace(/-/g, '_')}.webp`,
})

export const ANIMATIONS = [
  animation('idle', 'Idle', { actionId: 0, tripoPreset: 'preset:idle' }),
  animation('idle-02', 'Idle 2', { actionId: 11, tripoPreset: 'preset:idle' }),
  animation('alert', 'Alert', { actionId: 2 }),
  animation('arise', 'Arise', { actionId: 3 }),
  animation('idle-turn', 'Idle turn', { actionId: 576, tripoPreset: 'preset:turn' }),
  animation('agree-gesture', 'Agree gesture', { actionId: 25 }),
  animation('big-wave-hello', 'Big wave hello', { actionId: 28 }),
  animation('big-heart-gesture', 'Big heart gesture', { actionId: 27 }),
  animation('call-gesture', 'Call gesture', { actionId: 29 }),
  animation('angry-stomp', 'Angry stomp', { actionId: 26 }),
  animation('attack', 'Attack', { actionId: 4, tripoPreset: 'preset:slash' }),
  animation('draw-and-shoot', 'Draw and shoot', { actionId: 222, tripoPreset: 'preset:shoot' }),
  animation('skill-01', 'Skill 1', { actionId: 17 }),
  animation('skill-02', 'Skill 2', { actionId: 18 }),
  animation('be-hit-fly-up', 'Be hit fly up', { actionId: 7, tripoPreset: 'preset:hurt' }),
  animation('dead', 'Dead', { actionId: 8 }),
  animation('jump-run', 'Jump run', { actionId: 13, tripoPreset: 'preset:jump' }),
  animation('run-fast', 'Run fast', { actionId: 16, tripoPreset: 'preset:run' }),
  animation('run-02', 'Run 2', { actionId: 14, tripoPreset: 'preset:run' }),
  animation('back-left-run', 'Back left run', { actionId: 5 }),
  animation('back-right-run', 'Back right run', { actionId: 6 }),
  animation('forward-left-run-fight', 'Forward left run fight', { actionId: 9 }),
  animation('walk-fight-forward', 'Walk fight forward', { actionId: 21 }),
  animation('walk-fight-back', 'Walk fight back', { actionId: 20 }),
  animation('walking-woman', 'Walking woman', { actionId: 1, tripoPreset: 'preset:walk' }),
  animation('ladder-climb', 'Ladder climb', { actionId: 438, tripoPreset: 'preset:climb' }),
  animation('dive-down', 'Dive down', { actionId: 506, tripoPreset: 'preset:dive' }),
  animation('fall', 'Fall', { actionId: 502, tripoPreset: 'preset:fall' }),
  animation('funny-dancing-01', 'Funny dancing 1', { actionId: 22 }),
  animation('funny-dancing-02', 'Funny dancing 2', { actionId: 23 }),
]

const ANIMATIONS_BY_ID = new Map(ANIMATIONS.map(entry => [entry.id, entry]))
const ANIMATIONS_BY_ACTION_ID = new Map(ANIMATIONS.map(entry => [entry.actionId, entry]))
// The idle and run presets are each claimed by two entries, so the reverse
// lookup keeps the first one declared rather than the last.
const ANIMATIONS_BY_TRIPO_PRESET = new Map(
  ANIMATIONS.filter(entry => entry.tripoPreset).reverse().map(entry => [entry.tripoPreset, entry]),
)

/**
 * Looks up a library entry by its local slug.
 *
 * @param {string|null} [id] - ANIMATIONS[].id, e.g. the stored animateSettings.animationId
 * @returns {Object|undefined} - The matching entry, or undefined when nothing is selected
 */
export const findAnimationById = id => ANIMATIONS_BY_ID.get(id)

/**
 * Looks up a library entry by the Meshy numeric library id persisted on a run
 * (provider_run.action_code), so a reopened run can highlight its animation.
 *
 * @param {number|string} [actionId] - Meshy action_id
 * @returns {Object|undefined}
 */
export const findAnimationByActionId = actionId => ANIMATIONS_BY_ACTION_ID.get(Number(actionId))

/**
 * Looks up a library entry by the Tripo3D preset persisted on a run
 * (provider_run.animation).
 *
 * @param {string} [tripoPreset] - Tripo3D preset name, e.g. "preset:walk"
 * @returns {Object|undefined}
 */
export const findAnimationByTripoPreset = tripoPreset => ANIMATIONS_BY_TRIPO_PRESET.get(tripoPreset)
